import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Package,
  Percent,
  IndianRupee,
  CalendarRange,
  Receipt,
  RefreshCw,
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { fintechApi } from "../services/api";

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Backend kabhi array, kabhi page object bhejta hai
const toArray = (res) =>
  Array.isArray(res)
    ? res
    : Array.isArray(res?.content)
    ? res.content
    : Array.isArray(res?.data)
    ? res.data
    : Array.isArray(res?.data?.content)
    ? res.data.content
    : Array.isArray(res?.products)
    ? res.products
    : [];

// Sirf wahi key uthao jo API ne sach me bheji hai — warna undefined
const pick = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const isNum = (v) => v !== undefined && v !== null && v !== "" && !isNaN(Number(v));

// Ek product ke liye sirf available fields ki rows banao (koi placeholder nahi)
const buildRows = (p) => {
  const rows = [];

  const minRate = pick(p, ["minInterestRate", "interestRateMin", "minRate"]);
  const maxRate = pick(p, ["maxInterestRate", "interestRateMax", "maxRate"]);
  const flatRate = pick(p, ["interestRate", "rateOfInterest"]);
  if (isNum(minRate) && isNum(maxRate)) {
    rows.push({ icon: Percent, label: "Interest Rate", value: `${minRate}% - ${maxRate}% p.a.` });
  } else if (isNum(minRate) || isNum(maxRate) || isNum(flatRate)) {
    const r = isNum(flatRate) ? flatRate : isNum(minRate) ? minRate : maxRate;
    rows.push({ icon: Percent, label: "Interest Rate", value: `${r}% p.a.` });
  }

  const minAmt = pick(p, ["minAmount", "minLoanAmount", "minimumAmount"]);
  const maxAmt = pick(p, ["maxAmount", "maxLoanAmount", "maximumAmount"]);
  if (isNum(minAmt) && isNum(maxAmt)) {
    rows.push({ icon: IndianRupee, label: "Loan Amount", value: `${formatINR(minAmt)} - ${formatINR(maxAmt)}` });
  } else if (isNum(minAmt)) {
    rows.push({ icon: IndianRupee, label: "Min Loan Amount", value: formatINR(minAmt) });
  } else if (isNum(maxAmt)) {
    rows.push({ icon: IndianRupee, label: "Max Loan Amount", value: formatINR(maxAmt) });
  }

  const minTen = pick(p, ["minTenure", "minTenureMonths", "minimumTenure"]);
  const maxTen = pick(p, ["maxTenure", "maxTenureMonths", "maximumTenure"]);
  if (isNum(minTen) && isNum(maxTen)) {
    rows.push({ icon: CalendarRange, label: "Tenure", value: `${minTen} - ${maxTen} months` });
  } else if (isNum(minTen)) {
    rows.push({ icon: CalendarRange, label: "Min Tenure", value: `${minTen} months` });
  } else if (isNum(maxTen)) {
    rows.push({ icon: CalendarRange, label: "Max Tenure", value: `${maxTen} months` });
  }

  const feePct = pick(p, ["processingFeePercent", "processingFeePercentage", "processingFeeRate"]);
  const feeAmt = pick(p, ["processingFee", "processingFeeAmount"]);
  if (isNum(feePct)) {
    rows.push({ icon: Receipt, label: "Processing Fee", value: `${feePct}%` });
  } else if (isNum(feeAmt)) {
    rows.push({ icon: Receipt, label: "Processing Fee", value: formatINR(feeAmt) });
  }

  return rows;
};

export default function LoanProducts() {
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "#2e856e";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  // Product config admin ka kaam hai — backend par bhi yahi check hona chahiye
  const { isAdmin, has } = usePermissions();
  const canManage = isAdmin || has("LOAN_PRODUCT_MANAGE");

  const openCreate = () => navigate("/fintech/loan-products/new");
  // Product state me bhejo — edit page ko list dobara nahi maangni padti
  const openEdit = (p) => navigate(`/fintech/loan-products/${p?.id ?? p?.productId}/edit`, { state: { product: p } });

  const handleDelete = (p) => {
    const pid = p?.id ?? p?.productId;
    const name = p?.productName || p?.name || `Product #${pid}`;
    Modal.confirm({
      title: `Delete "${name}"?`,
      content: "Existing loans keep their terms; the product just won't be offered for new applications.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await fintechApi.deleteLoanProduct(pid);
          message.success("Loan product deleted");
          fetchProducts();
        } catch (e) {
          console.error("Loan product delete failed:", e);
          message.error(e?.message || "Failed to delete loan product");
        }
      },
    });
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fintechApi.getLoanProducts();
      setProducts(toArray(res));
    } catch (err) {
      console.error("Loan products load failed:", err);
      // Sample catalogue kabhi nahi dikhayenge — error + Retry
      setLoadError(err.message || "Failed to load loan products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const term = searchTerm.trim().toLowerCase();
  const filteredProducts = products.filter((p) => {
    if (!term) return true;
    const name = String(pick(p, ["name", "productName", "productCode", "code"]) || "");
    const type = String(pick(p, ["loanType", "type", "category", "productType"]) || "");
    return name.toLowerCase().includes(term) || type.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <Package className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Loan Products</h1>
            <p className="text-xs md:text-sm text-gray-500">
              Configured loan schemes offered to applicants
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search product or type..."
              className="w-full md:w-64 pl-10 pr-4 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
            />
          </div>

          <button
            onClick={fetchProducts}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh Catalogue"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          {canManage && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              <Plus className="w-4 h-4" />
              <span>Add Product</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Loading ── */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm flex items-center justify-center py-20">
          <div
            className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: primaryColor, borderTopColor: "transparent" }}
          />
        </div>
      ) : loadError ? (
        /* ── Error + Retry ── */
        <div className="bg-white p-10 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center text-center gap-3">
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <h2 className="text-lg font-bold text-gray-900">Could not load loan products</h2>
          <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
          <button
            onClick={fetchProducts}
            className="mt-1 px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      ) : filteredProducts.length === 0 ? (
        /* ── Empty ── */
        <div className="bg-white p-12 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center text-center gap-2">
          <Package className="w-10 h-10 text-gray-300" />
          <p className="font-semibold text-gray-500">No loan products found</p>
          <p className="text-xs text-gray-400">
            {term ? "Try a different search term" : "The catalogue is empty or not configured yet"}
          </p>
          <button
            onClick={fetchProducts}
            className="mt-2 px-3 py-1.5 rounded-xl text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      ) : (
        /* ── Product Cards ── */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredProducts.map((p, idx) => {
            const name = pick(p, ["name", "productName", "productCode", "code"]);
            const loanType = pick(p, ["loanType", "type", "category", "productType"]);
            const description = pick(p, ["description", "remarks"]);
            const rows = buildRows(p);

            // Active flag tabhi dikhega jab API me actually aaya ho
            const rawActive = p?.active ?? p?.isActive ?? p?.enabled;
            const statusText = pick(p, ["status"]);
            let activeBadge = null;
            if (typeof rawActive === "boolean") {
              activeBadge = rawActive
                ? { label: "Active", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
                : { label: "Inactive", cls: "bg-gray-100 text-gray-600 border-gray-200" };
            } else if (statusText) {
              const up = String(statusText).toUpperCase();
              activeBadge = {
                label: up,
                cls:
                  up === "ACTIVE"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-gray-100 text-gray-600 border-gray-200",
              };
            }

            return (
              <div
                key={p?.id ?? p?.productId ?? idx}
                className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm p-5 flex flex-col gap-4"
              >
                {/* Card Head */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Package className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 truncate">
                        {name || `Product #${p?.id ?? idx + 1}`}
                      </h3>
                      {loanType && (
                        <span className="inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-gray-100 text-gray-700">
                          {loanType}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {activeBadge && (
                      <span className={`px-2 py-1 rounded-full text-xs font-bold border ${activeBadge.cls}`}>
                        {activeBadge.label}
                      </span>
                    )}
                    {canManage && (
                      <>
                        <button
                          onClick={() => openEdit(p)}
                          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                          title="Edit product"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(p)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete product"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {description && (
                  <p className="text-xs text-gray-500 leading-relaxed">{description}</p>
                )}

                {/* Detail Rows — sirf available fields */}
                {rows.length > 0 ? (
                  <div className="divide-y divide-gray-100 border-t border-gray-100 pt-1">
                    {rows.map((row) => {
                      const Icon = row.icon;
                      return (
                        <div key={row.label} className="flex items-center justify-between gap-3 py-2">
                          <span className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                            <Icon className="w-3.5 h-3.5 text-gray-400" />
                            {row.label}
                          </span>
                          <span className="text-xs font-bold text-gray-900 text-right">{row.value}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                    No pricing details returned for this product
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
